<i18n src="./resources/locales.json"></i18n>

<template>
  <div id="login_wrapper" :dark="$vuetify.theme.dark">
    <div v-if="isTransitionView || isLoadingAgentData">
      <v-progress-circular :width="2" :size="20" color="primary" indeterminate />
      <span class="pl-2">
        {{ isTransitionView ? $t('redirecting') : $t('loading') }}
      </span>
    </div>
    <v-card v-else class="card">
      <v-img height="250" src="@/assets/office.jpg"></v-img>
      <div class="message-container">
        <div class="logo">
          <v-img class="img-logo" :src="require('@/assets/xtium_white_x_mark.svg')" max-height="42" max-width="42" />
        </div>
        <div class="card-content">
          <div>
            <v-card-title class="oid-title">
              <div v-once>{{ $t('title') }}</div>
            </v-card-title>
            <v-card-text>
              <div v-once class="message">{{ $t('message') }}</div>
            </v-card-text>
          </div>
          <v-btn class="mr-6" color="primary" depressed @click="loginUsingOpenId()">
            {{ $t('loginUsingOpenId', { org }) }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { app, authentication } from '@microsoft/teams-js';
import { v4 as uuid } from 'uuid';
import { mapActions } from 'vuex';
import MSTeamsMixing from '@/mixins/ms-teams/MSTeamsMixing';

const CLEARLOGIN = {
  AUTH: {
    STATE: 'cl.state',
    SUCCESS: 'cl.success',
    ERROR: 'cl.error'
  }
};

const URL_PARAMS = {
  LOGIN_STEP: 'loginStep',
  STATE: 'state',
  ERROR: 'error',
  CODE: 'code',
  ORG: 'org'
};

export default {
  name: 'EcsLoginOpenId',

  mixins: [MSTeamsMixing],

  data() {
    return {
      initializeRef: null,
      code: '',
      org: '',
      loggedIn: false,
      isLoadingAgentData: false
    };
  },

  computed: {
    urlSearchParams() {
      return new URLSearchParams(window.location.search);
    },
    isTransitionView() {
      return this.urlSearchParams.get(URL_PARAMS.LOGIN_STEP) === 'start' || localStorage.getItem('sessionId');
    }
  },

  created() {
    this.initializeRef = app.initialize();
  },

  mounted() {
    this.org = this.urlSearchParams.get(URL_PARAMS.ORG);
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      sessionStorage.setItem('sessionId', sessionId);

      // Move stored local storage data to session storage
      sessionStorage.setItem('agent', localStorage.getItem('agent'));
      sessionStorage.setItem('organizationData', localStorage.getItem('organizationData'));
      sessionStorage.setItem('systemData', localStorage.getItem('systemData'));

      return this.$router.push('/');
    }

    switch (this.urlSearchParams.get(URL_PARAMS.LOGIN_STEP)) {
      case 'start':
        this.startAuth();
        break;
      case 'finish':
        this.finishAuth();
        break;
    }
  },

  methods: {
    ...mapActions('AGENT', ['loginAgent']),

    getContext() {
      return app && app.getContext();
    },

    loginUsingOpenId() {
      const params = `${URL_PARAMS.LOGIN_STEP}=start&${URL_PARAMS.ORG}=${this.org}`;
      const loginStartUrl = `${window.location.origin}/teams?${params}`;

      authentication
        .authenticate({
          url: loginStartUrl,
          width: 600,
          height: 720
        })
        .then(async code => {
          this.code = code;
          this.loggedIn = true;
          await this.retrieveEcsSession(code);
        })
        .catch(() => {
          this.loggedIn = false;
        });
    },

    async retrieveEcsSession(code) {
      this.isLoadingAgentData = true;
      await this.loginAgent({ org: this.org, code, teams: true });
      await this.subscribeToPresenceChanges(code);
      this.$router.push('/');
    },

    generateState() {
      return uuid();
    },

    startAuth() {
      app.getContext().then(() => {
        const state = this.generateState();
        console.log('startAuth STATE', state);
        localStorage.setItem(CLEARLOGIN.AUTH.STATE, state);
        localStorage.removeItem(CLEARLOGIN.AUTH.ERROR);
        const params = `${URL_PARAMS.ORG}=${this.org}&${URL_PARAMS.STATE}=${state}`;
        window.location.href = `${window.location.origin}/api/openid/start-auth?${params}`;
      });
    },

    /**
     * This is the view rendered when clearlogin is redirecting with code=whatever.
     * @return {void}
     */
    async finishAuth() {
      await this.initializeRef;

      if (this.urlSearchParams.get(URL_PARAMS.ERROR)) {
        localStorage.setItem(CLEARLOGIN.AUTH.ERROR, this.urlSearchParams.get(URL_PARAMS.ERROR));
        return authentication.notifyFailure('UnknownError');
      }

      const expectedState = localStorage.getItem(CLEARLOGIN.AUTH.STATE);

      if (expectedState !== this.urlSearchParams.get(URL_PARAMS.STATE)) {
        const failureReason = 'StateDoesNotMatch';
        localStorage.setItem(CLEARLOGIN.AUTH.ERROR, failureReason);
        return authentication.notifyFailure(failureReason);
      }

      const key = CLEARLOGIN.AUTH.SUCCESS;
      const code = this.urlSearchParams.get(URL_PARAMS.CODE);
      const value = JSON.stringify({ code });
      localStorage.setItem(key, value);
      authentication.notifySuccess(code);
    }
  }
};
</script>

<style scoped src="./scoped.scss" lang="scss" />
